/* ==========================================================================
   ImageRxjs > Template
   ========================================================================== */

/* Import modules */
const { html } = require('common-tags');

const ViewHelpers = require('@bluekiri/alexandria/core/ViewHelpers');
const { prepareDataConfig } = require('@bluekiri/alexandria/helpers/dataHelpers');

const configuration = require('./client/js/config');

/* Get resize config */
const buildResizeConfig = resize => {
    if (!resize) return false;

    return {
        ...configuration().resize,
        ...resize
    };
};

/* Build src */
const buildSrc = (data, context) => {
    /* Data */
    const { src, resize = false, outsider = false } = data;

    if (!src) return '';

    /* View helpers */
    const { imageResizer, asset } = ViewHelpers(context);

    if (outsider) {
        return src;
    }

    return resize ? imageResizer(src, buildResizeConfig(resize)) : asset(src);
};

/* Build alternative */
const buildAlternative = (data, context) => {
    /* Data */
    const { alternative, resize = false, outsider = false } = data;

    /* View helpers */
    const { imageResizer, asset } = ViewHelpers(context);

    if (!alternative) {
        return imageResizer(configuration().lazySrc, buildResizeConfig(resize));
    }

    if (outsider) {
        return alternative;
    }

    return resize ? imageResizer(alternative, buildResizeConfig(resize)) : asset(alternative);
};

/* Render link */
const renderLink = link => {
    /* Data */
    const { href, title = false, target = false } = link;

    return html(`
        <a href='${href}'
            ${title ? `title='${title}'` : ''}
            ${target ? `target='${target}'` : ''}>
        </a>
    `);
};

/* Function print image attributes */
const getImageAttr = data => {
    const { alt, title = false, width = false, height = false } = data;
    const renderAlt = alt ? `alt='${alt}'` : '';
    const renderTitle = title ? `title='${title}'` : '';
    const renderWidth = width ? `width='${width}'` : '';
    const renderHeight = height ? `height='${height}'` : '';

    return `${renderAlt} ${renderTitle} ${renderWidth} ${renderHeight}`;
};

/* Render lazy image */
const renderLazyImage = (data, context) => {
    /* Data */
    const { resize = false } = data;

    /* View helpers */
    const { imageResizer } = ViewHelpers(context);

    return html(`
        <img src='${imageResizer(`${configuration().lazySrc}`, buildResizeConfig(resize))}'
            data-src='${buildSrc(data, context)}'
            data-alternative='${buildAlternative(data, context)}'
            ${getImageAttr(data)}
        />
    `);
};

/* Render not lazy image */
const renderNotLazyImage = (data, context) => {
    /* Data */

    const srcParam = buildSrc(data, context);
    const src = !srcParam ? '' : `src='${srcParam}'`;

    return html(
        `<img
            ${src}
            ${getImageAttr(data)}
        />`
    );
};

/**
 * ImageRxjs template
 * @param {object} data Object containing the data model for this component.
 * @returns {string} Html
 */
module.exports = function Template(data = {}, context = {}) {
    /* Data */
    const { config = {}, link, figcaption } = data;
    const { styles = [], isLazy = true } = config;
    const { dataType } = prepareDataConfig(config.data);

    /* Template */
    return html(`
        <figure class='${['image-rxjs', ...styles].join(' ').trim('')}'
            data-component='${configuration().component}'
            ${dataType ? `data-type='${dataType}'` : ''}
            data-config='${JSON.stringify({
                ...config,
                alternative: buildAlternative(data, context)
            })}'>
            ${isLazy ? renderLazyImage(data, context) : renderNotLazyImage(data, context)}
            ${figcaption ? `<figcaption>${figcaption}</figcaption>` : ''}
            ${link ? renderLink(link) : ''}
        </figure>
    `);
};
